import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface LegalIssuesProps {
  data?: any; // For backward compatibility
  onChange?: (data: any) => void; // For backward compatibility
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  has_fines: string;
  fines_description: string;
  has_probation: string;
  probation_description: string;
  has_felonies: string;
  felonies_description: string;
  has_misdemeanors: string;
  misdemeanors_description: string;
  has_incarceration: string;
  incarceration_details: string;
}

const LegalIssues: React.FC<LegalIssuesProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  // Use new props if available, otherwise fall back to old props
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => {
      /* noop */
    });
  const [formData, setFormData] = useState<FormData>({
    has_fines: '',
    fines_description: '',
    has_probation: '',
    probation_description: '',
    has_felonies: '',
    felonies_description: '',
    has_misdemeanors: '',
    misdemeanors_description: '',
    has_incarceration: '',
    incarceration_details: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'legal_issues', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'legal_issues', fieldMappings);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Legal Issues
        {readOnly && (
          <Typography
            component='span'
            variant='body2'
            color='warning.main'
            sx={{ ml: 2, fontStyle: 'italic', fontWeight: 'bold' }}
          >
            (Read Only - View Only)
          </Typography>
        )}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        opacity: readOnly ? 0.8 : 1
      }}>
        {/* Fines */}
        {!isFieldHiddenLocal('has_fines') && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Fines
            </Typography>
            <FormControl component='fieldset' sx={{ mb: 2 }}>
              <RadioGroup
                value={formData.has_fines}
                onChange={e => handleChange('has_fines', e.target.value)}
              >
                <FormControlLabel value='yes' control={<Radio disabled={readOnly} />} label='Yes' />
                <FormControlLabel value='no'  control={<Radio disabled={readOnly} />} label='No' />
              </RadioGroup>
            </FormControl>
            {formData.has_fines === 'yes' && !isFieldHiddenLocal('fines_description') && (
              <TextField
                fullWidth
                label='Describe'
                multiline
                rows={3}
                value={formData.fines_description}
                onChange={e => handleChange('fines_description', e.target.value)}
                required={isFieldRequiredLocal('fines_description')}
                disabled={readOnly}
              />
            )}
          </Box>
        )}

        {/* Probation */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Probation
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_probation}
              onChange={e => handleChange('has_probation', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_probation === 'yes' && (
            <TextField
              fullWidth
              label='Describe'
              multiline
              rows={3}
              value={formData.probation_description}
              onChange={e =>
                handleChange('probation_description', e.target.value)
              }
            />
          )}
        </Box>

        {/* Felonies */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Felonies
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_felonies}
              onChange={e => handleChange('has_felonies', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_felonies === 'yes' && (
            <TextField
              fullWidth
              label='Describe'
              multiline
              rows={3}
              value={formData.felonies_description}
              onChange={e =>
                handleChange('felonies_description', e.target.value)
              }
            />
          )}
        </Box>

        {/* Misdemeanors */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Misdemeanors
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_misdemeanors}
              onChange={e => handleChange('has_misdemeanors', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_misdemeanors === 'yes' && (
            <TextField
              fullWidth
              label='Describe'
              multiline
              rows={3}
              value={formData.misdemeanors_description}
              onChange={e =>
                handleChange('misdemeanors_description', e.target.value)
              }
            />
          )}
        </Box>

        {/* Incarceration */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Have you ever been incarcerated?
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_incarceration}
              onChange={e => handleChange('has_incarceration', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_incarceration === 'yes' && (
            <TextField
              fullWidth
              label='If Yes Please mention the range of time. (Please mention all incarcerations)'
              multiline
              rows={4}
              value={formData.incarceration_details}
              onChange={e =>
                handleChange('incarceration_details', e.target.value)
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LegalIssues;
